<template>
	<v-dialog v-model="userSettingOpened" fullscreen hide-overlay transition="dialog-bottom-transition">
		<v-card>
			<v-toolbar dark class="page-header">
				<v-btn icon dark @click.native="onCloseClicked">
					<v-icon>fa-times</v-icon>
				</v-btn>
				<v-toolbar-title>
					{{ $t('message.userSettings') }}
				</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-toolbar-items>
					<v-btn dark text @click.native="saveUserSettings" class="pa-0 ma-0">
						Save
					</v-btn>
				</v-toolbar-items>
			</v-toolbar>
			<!-- Settings -->
			<v-layout row wrap>
				<v-flex xs12>
					<v-card-title class="pb-0 pl-6">
						<div class="headline">
							{{ $t('message.notificationSettings1') }}
						</div>
					</v-card-title>
				</v-flex>
				<v-flex xs12 pl-12>
					<v-card-text>
						<div class="subheader my-1">
							{{ $t('message.notificationSettings2') }}
						</div>
						<p class="font-xs mb-0">
							{{ $t('message.notificationSettings12') }}
						</p>
						<v-layout row wrap>
							<v-flex xs12 sm6>
								<template>
									<v-container fluid grid-list-sm>
										<v-data-iterator
											:items="notificationTypes"
											item-key="code"
											row
											wrap
											:hide-default-footer="true"
										>
											<template v-slot:default="props">
												<v-row>
													<v-col
														v-for="item in props.items.filter(
															i => !isHiddenNotification(i.code)
														)"
														:key="item.code"
														cols="12"
														sm="6"
														md="4"
														lg="4"
													>
														<v-card>
															<v-card-title>
																<h5>{{ item.description }}</h5>
															</v-card-title>
															<v-switch
																v-for="(v, k) in notificationDeliveryTypes"
																:key="`notificationDeliveryType-${k}`"
																:label="v.description"
																:value="`${item.id}_${v.id}`"
																class="pl-3 mt-0"
																v-model="notifications"
															></v-switch>
														</v-card>
													</v-col>
												</v-row>
											</template>
										</v-data-iterator>
									</v-container>
								</template>
							</v-flex>
						</v-layout>
					</v-card-text>
				</v-flex>
			</v-layout>
			<v-divider v-if="showCancellationFeature"></v-divider>
			<v-layout row wrap v-if="showCancellationFeature">
				<v-flex xs12>
					<v-card-title class="pb-0 pl-6">
						<div class="headline">
							{{ $t( "profile.UserSettings_cancelAccountTitle" ) }}
						</div>
					</v-card-title>
				</v-flex>
				<v-flex xs12 pl-12>
					<v-card-text>
						<p>
							{{ $t( "profile.UserSettings_cancelAccountDescription1" ) }}
						</p>
						<p>
							{{ $t( "profile.UserSettings_cancelAccountDescription2" ) }}
						</p>
						<v-alert :value="deleteAccountManagerOfTeam" type="error">
							{{ $t( "profile.UserSettings_cancelAccountAdminRoleWarning" ) }}
						</v-alert>
						<p>
							<v-btn color="error" @click="showBeforeYouGo = true">{{ $t( "profile.UserSettings_cancelAccountButton" ) }}</v-btn>
						</p>
						<v-flex xs6 v-if="showBeforeYouGo">
							<h4>
								<strong>
									{{ $t( "profile.UserSettings_cancelAccountPromptTitle" ) }}
								</strong>
							</h4>
							<p>
								{{ $t( "profile.UserSettings_cancelAccountPromptDescription1" ) }}
							</p>
							<p>
								{{ $t( "profile.UserSettings_cancelAccountPromptDescription2" ) }}
							</p>
							<div class="user-settings__delete-reason-list">
								<div v-for="( reason, index ) in deletionReasons" :key="reason" class="user-settings__delete-reason-row">
									<TeamingCheckbox
										name="deletionReason"
										:value="`${ index }`"
										:is-checked="isCancelReasonChecked( index )"
										@change="onCancelReasonChange"
									/>
									<div>{{ reason }}</div>
								</div>
							</div>
							<v-alert :value="canSubmitCancellation" type="error">
								{{ $t( "profile.UserSettings_cancelAccountPromptValidationError" ) }}
							</v-alert>
							<p>
								{{ $t( "profile.UserSettings_cancelAccountPromptDescription3" ) }}
							</p>
							<v-textarea outlined v-model="deletionComment"></v-textarea>
							<v-alert :value="deleteAccountManagerOfTeam" type="error">
								{{ $t( "profile.UserSettings_cancelAccountPromptManagerError" ) }}
							</v-alert>
							<v-layout row>
								<v-flex xs6>
									<v-btn color="info"
										@click="abortCancelAccount"
									>{{ $t( "profile.UserSettings_cancelAccountPromptCancelButton" ) }}</v-btn>
								</v-flex>
								<v-flex xs6>
									<v-btn color="error"
										@click="cancelAccount"
									>{{ $t( "profile.UserSettings_cancelAccountPromptConfirmButton" ) }}</v-btn>
								</v-flex>
							</v-layout>
						</v-flex>
					</v-card-text>
				</v-flex>
			</v-layout>
			<v-layout row wrap v-else>
				<v-flex xs12>
					<v-card-title class="pb-0 pl-6">
						<div class="headline">
							{{ $t( "profile.UserSettings_cancelAccountTitle" ) }}
						</div>
					</v-card-title>
				</v-flex>
				<v-flex xs12 pl-12>
					<v-card-text>{{ $t( "profile.UserSettings_cancelAccountPendingCancellationMessage" ) }}</v-card-text>
				</v-flex>
			</v-layout>
		</v-card>
	</v-dialog>
</template>

<script>
/* eslint-disable camelcase */
import range from "lodash/range";
import { mapGetters, mapMutations } from "vuex";
import { UserSettingsOpenedEvent, UserAccountCancelledEvent } from "@/telemetry";
import TeamingCheckbox from "@/components/Common/TeamingCheckbox";

// NOTE: this is only temporary for this particular use case, I expect
// this to be unnecessary in the future, once we have a better bead
// on cancellation reasons, and a fully fledged cancellation process
const storageAPI = {
	disableCancellationFeature( { personId, } ) {
		window.localStorage.setItem( `teaming:${ personId }:showAccountCancellationFeature`, "false" );
	},
	isCancellationFeatureEnabled( { personId, } ) {
		return window.localStorage.getItem( `teaming:${ personId }:showAccountCancellationFeature` ) !== "false";
	},
};

export default {
	components: {
		TeamingCheckbox,
	},
	data() {
		return {
			deleteAccountManagerOfTeam: false,
			checkedCancellationReasons: {},
			notifications: [],
			deletionComment: null,
			showBeforeYouGo: false,
			isCancellationValidateable: false,
			showCancellationFeature: true,
		};
	},
	computed: {
		...mapGetters( "auth", [ "user", ] ),
		...mapGetters( "sidebar", [ "userSettingOpened", ] ),
		notificationSettings() {
			return this.$store.getters.settings.notifications;
		},
		notificationTypes() {
			return this.$store.getters.settings.notificationTypes;
		},
		notificationDeliveryTypes() {
			return this.$store.getters.settings.notificationDeliveryTypes;
		},
		deletionReasons() {
			// eslint-disable-next-line no-magic-numbers
			return range( 0, 10 ).map( i => {
				return this.$t( `profile.UserSettings_cancelAccountPromptReason${ i }` );
			} );
		},
		selectedCancellationReasons() {
			return Object.keys( this.checkedCancellationReasons )
				.filter( index => !!this.checkedCancellationReasons[ index ] );
		},
		canSubmitCancellation() {
			return this.isCancellationValidateable &&
				this.selectedCancellationReasons.length === 0;
		},
	},
	methods: {
		...mapMutations( "confirmationDialog", [ "requestUserConfirmation", ] ),
		isHiddenNotification( code ) {
			return [ "USER_INVITE", "USER_TOKEN_REFRESH_REQUIRED", ].indexOf( code ) >= 0;
		},
		async saveUserSettings() {
			// find enabled/disabled status
			const notifsToUpdate = [];
			for ( const i1 of this.notificationTypes ) {
				for ( const i2 of this.notificationDeliveryTypes ) {
					notifsToUpdate.push( {
						notificationTypeId: i1.id,
						notificationDeliveryTypeId: i2.id,
						enabled: this.notifications.indexOf( `${i1.id}_${i2.id}` ) >= 0,
					} );
				}
			}

			// update settings
			await this.$store.dispatch( "setSettings", {
				notifications: notifsToUpdate,
			} );
			this.dialog = false;
		},
		isCancelReasonChecked( index ) {
			return !!this.checkedCancellationReasons[ index ];
		},
		onCancelReasonChange( { isChecked, value, } ) {
			this.checkedCancellationReasons = {
				...this.checkedCancellationReasons,
				[ value ]: isChecked,
			};
		},
		resetCancelAccountState() {
			this.isCancellationValidateable = false;
			this.checkedCancellationReasons = {};
			this.showBeforeYouGo = false;
			this.deletionComment = "";
		},
		abortCancelAccount() {
			this.resetCancelAccountState();
		},
		async cancelAccount() {
			this.isCancellationValidateable = true;

			if ( this.selectedCancellationReasons.length === 0 ) {
				return;
			}

			const reasons = this.selectedCancellationReasons
				.map( index => this.$t( `profile.UserSettings_cancelAccountPromptReason${ index }` ) );
			const comments = this.deletionComment || "";
			const { personId, email, } = this.user;

			const telemetryEvent = new UserAccountCancelledEvent( email, reasons, comments );
			await telemetryEvent.submit();

			await this.$intercom.userCancellationRequest( personId, reasons, comments );

			// not really confirmation, just using this as a notification that the cancellation
			// request has been submitted
			this.requestUserConfirmation( {
				header: {
					text: "profile.UserSettings_cancelAccountConfirmationTitle",
				},
				body: {
					text: [
						"profile.UserSettings_cancelAccountConfirmationDescription1",
						"profile.UserSettings_cancelAccountConfirmationDescription2",
					],
				},
				confirm: {
					buttonText: "profile.UserSettings_cancelAccountConfirmationOKButton",
					callback: () => {
						this.showCancellationFeature = false;
						storageAPI.disableCancellationFeature( this.user );
						this.resetCancelAccountState();
					},
				},
				cancel: {
					// hide the cancel button, don't need it
					buttonText: "",
				},
			} );
		},
		onCloseClicked() {
			this.$store.commit( "sidebar/userSettingOpened", false );
		},
	},
	watch: {
		notificationSettings( val ) {
			// default notifications to true
			for ( const i1 of this.notificationTypes ) {
				for ( const i2 of this.notificationDeliveryTypes ) {
					this.notifications.push( `${i1.id}_${i2.id}` );
				}
			}
			// remove any that are set to false
			for ( const row of val ) {
				if ( !row.enabled ) {
					const index = this.notifications.indexOf(
						`${row.notificationTypeId}_${row.notificationDeliveryTypeId}`
					);
					this.notifications.splice( index, 1 );
				}
			}
		},
		userSettingOpened( isOpen ) {
			if ( isOpen ) {
				const telemetryEvent = new UserSettingsOpenedEvent();
				telemetryEvent.submit();
			} else {
				this.resetCancelAccountState();
			}
		},
	},
	mounted() {
		this.$store.dispatch( "getSettings" );
		this.showCancellationFeature = storageAPI.isCancellationFeatureEnabled( this.user );
	},
};
</script>

<style lang="scss" scoped>
.headline {
	font-size: $font-size-base;
	color: $mc-color;
}
.subheader {
	font-size: $font-size-md;
	font-weight: $font-weight-semi-bold;
}
.target-input-box {
	flex: 0 0 60px;
}
.material-icons {
	vertical-align: middle;
}
.pointer {
	cursor: pointer;
	color: $mc-color;
}
.v-data-iterator {
	.col-sm-6 {
		padding: 1px;
	}
	.v-card {
		height: 100%;
	}
	.v-card__title {
		word-break: break-word;
	}
}

.user-settings__delete-reason-list {
	padding-bottom: 1rem;
}

.user-settings__delete-reason-row {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 1rem;
	padding: 0.2rem;

	&:first-of-type {
		padding-top: 0;
	}

	&:last-of-type {
		padding-bottom: 0;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/UserSettings.vue